import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Palette, Layout, Smartphone, Rocket, Search, Sparkles, Gauge, ShieldCheck } from "lucide-react";
import WaveBackground from "@/components/site/WaveBackground";
import BundleCTA from "@/components/site/BundleCTA";
import useSEO from "@/lib/useSEO";
import { IMG } from "@/lib/images";

const features = [
  { icon: Palette, title: "UI/UX design", desc: "Elegant, considered interfaces designed around your customers — not templated layouts." },
  { icon: Layout, title: "Wireframing & prototyping", desc: "Low-fidelity flows first, so we agree the shape before we commit to pixels." },
  { icon: Smartphone, title: "Fully responsive", desc: "Beautiful on a 27-inch iMac and a 5-year-old Android phone. Same care, both." },
  { icon: Search, title: "SEO-ready foundation", desc: "Semantic markup, sitemap, meta tags, page-speed conscious — Google-friendly from day one." },
  { icon: Gauge, title: "Fast & lightweight", desc: "Modern stack, optimised images, sub-second load times. No bloat, no tracker soup." },
  { icon: ShieldCheck, title: "GDPR-friendly by default", desc: "Cookie consent, privacy policy, contact forms that don't leak — done properly." },
];

const included = [
  "Discovery & scoping call",
  "Custom UI/UX design",
  "Wireframes & interactive prototype",
  "Responsive build (desktop, tablet, mobile)",
  "SEO metadata & sitemap",
  "Contact form + spam protection",
  "Analytics setup",
  "Domain, hosting & DNS",
  "Post-launch support",
];

export default function WebsiteDesign() {
  useSEO({
    title: "Website Design — Elegant, considered sites for UK businesses | Fenco Digital",
    description: "Bespoke website design: UI/UX, wireframing, prototyping, fully responsive builds. SEO-ready, fast, GDPR-friendly. From consultation to launch.",
    path: "/website-design",
  });

  return (
    <div data-testid="website-design-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-24">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7 fade-up">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">Website design</p>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900 mt-6">
                Websites that look<br />the way you sound<span className="text-[#FF5A1F]">.</span>
              </h1>
              <p className="mt-7 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                Bespoke sites designed around your brand and your customers — not templated, not stock. Fast, elegant, responsive and SEO-ready from the moment you go live.
              </p>

              <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3.5 max-w-xl" data-testid="web-selling-points">
                {[
                  "Bespoke UI/UX design",
                  "Wireframes & prototypes",
                  "Fully responsive builds",
                  "SEO-ready foundation",
                  "GDPR-friendly by default",
                  "Ongoing support after launch",
                ].map((p) => (
                  <div key={p} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-gray-700">{p}</span>
                  </div>
                ))}
              </div>

              <div className="mt-11 flex flex-wrap gap-4">
                <Link
                  to="/contact?interest=Website%20Design"
                  data-testid="web-cta-quote"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all duration-300 shadow-lg shadow-orange-500/25 hover:-translate-y-0.5"
                >
                  Get a website quote <ArrowRight size={18} />
                </Link>
                <Link
                  to="/app-design"
                  data-testid="web-cta-app"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                >
                  Explore app design
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end fade-up">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-10 bg-[#FF5A1F]/10 blur-3xl rounded-full" />
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-gray-100">
                  <img
                    src={IMG.webHero}
                    alt="A modern designer's workstation with a dark iMac showing a web layout"
                    className="w-full h-full object-cover"
                    data-testid="web-hero-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-28 md:py-36 bg-[#F8F9FA]" data-testid="web-features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">What we do</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-5 leading-[1.05]">
              Considered design, top to bottom.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`web-feature-${i}`}
                className="bg-white rounded-2xl p-9 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-6">
                  <f.icon className="text-[#FF5A1F]" size={22} />
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900">{f.title}</h3>
                <p className="mt-3.5 text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-28 md:py-36 bg-white" data-testid="web-included-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">What&rsquo;s included</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-5 leading-[1.05]">
                A proper launch, not a rushed handover.
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                Every project includes the essentials done properly — no last-minute surprises about hosting, SEO or GDPR.
              </p>
              <ul className="mt-9 grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                {included.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img src={IMG.webDesk} alt="Web designer working on a modern layout on a MacBook" className="w-full h-full object-cover" loading="lazy" data-testid="web-desk-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-28 md:py-36 bg-[#0a0a0a] text-white relative overflow-hidden" data-testid="web-process-section">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF5A1F] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">Our process</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-5 leading-[1.05]">
              Four steps. Zero drama.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              ["01", "Discovery", "We learn how you trade and what your customers actually need to do on the site."],
              ["02", "Design", "Wireframes first, then polished visuals. You sign off before we write a line of code."],
              ["03", "Build", "Modern stack, semantic markup, tested on every device that matters."],
              ["04", "Launch", "DNS, hosting, analytics, backups — we handle it all. You just show up on go-live day."],
            ].map(([n, t, d], i) => (
              <div key={n} data-testid={`web-step-${i}`} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500">
                <p className="font-display text-4xl font-extrabold text-[#FF5A1F]">{n}</p>
                <h3 className="font-display text-lg font-bold mt-5">{t}</h3>
                <p className="text-gray-300 mt-2.5 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUNDLE CROSS-SELL */}
      <BundleCTA />

      {/* FINAL CTA */}
      <section className="py-28 md:py-36 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Sparkles className="mx-auto text-[#FF5A1F] mb-7" size={34} strokeWidth={1.5} />
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
            Time for a proper site<span className="text-[#FF5A1F]">?</span>
          </h2>
          <p className="mt-7 text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Tell us about your business and we&rsquo;ll come back with a scope and quote within one working day.
          </p>
          <Link
            to="/contact?interest=Website%20Design"
            data-testid="web-final-cta"
            className="mt-11 inline-flex items-center gap-2 px-9 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-xl shadow-orange-500/25"
          >
            Start a project <Rocket size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
